import { UpdateOrganizationPreferencesInterfaceInput } from '@/__generated__/graphql';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { TextFieldController } from '@components/controllers/TextFieldController';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';
import { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useModalStatusMessage } from 'src/hooks';
import UPDATE_ORGANIZATION_PREFERENCES_INTERFACE from '../graphql/mutation/updateOrganizationPreferencesInterface';
import GET_ORGANIZATION_PREFERENCES_INTERFACE from '../graphql/queries/getOrganizationPreferencesInterface';
import { TabProps } from '../types';

type FieldValues = UpdateOrganizationPreferencesInterfaceInput;

const InterfaceTab: FC<TabProps> = ({ userId }) => {
  const { _ } = useLingui();
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();
  const { data } = useSuspenseQuery(GET_ORGANIZATION_PREFERENCES_INTERFACE, {
    variables: {
      input:
        userId != null
          ? {
              id: userId,
            }
          : null,
    },
    fetchPolicy: 'no-cache',
  });
  const [update] = useMutation(UPDATE_ORGANIZATION_PREFERENCES_INTERFACE, {
    onCompleted: (data) => {
      openModalSuccessMessage(_(msg`Preferencias actualizadas correctamente`));
      const { id, ...defaultValues } = data.updateOrganizationPreferencesInterface;
      methods.reset({ ...defaultValues, organizationId: id });
    },
    onError: (error) => {
      openModalErrorMessage(error.message);
    },
  });

  const defaultValues: FieldValues = {
    organizationId: data.organization.id,
    defaultIndexRoute: data.organization.preferences.interface.defaultIndexRoute,
  };

  const methods = useForm<FieldValues>({
    defaultValues,
  });

  const submitHandler: SubmitHandler<FieldValues> = async (data) => {
    await update({
      variables: {
        input: data,
      },
    });
  };

  const resetHandler = () => {
    methods.reset(defaultValues);
  };

  const factoryDefaultsHandler = () => {
    methods.reset(
      {
        defaultIndexRoute: data.organization.preferences.defaultInterface.defaultIndexRoute,
        organizationId: data.organization.id,
      },
      {
        keepDefaultValues: true,
      },
    );
  };

  useEffect(() => {
    methods.reset(defaultValues);
  }, [data]);

  const { formState } = methods;

  return (
    <form style={{ height: '100%' }} onSubmit={methods.handleSubmit(submitHandler)}>
      <Box
        height="100%"
        gap={2}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box width="100%" display="flex" flexDirection="column" gap={2}>
          <TextFieldController
            control={methods.control}
            label={_(msg`Página de inicio`)}
            name="defaultIndexRoute"
            slotProps={{
              textField: {
                placeholder: 'control-center',
                size: 'small',
              },
            }}
          />
        </Box>

        <Box display="flex" justifyContent="end" gap={1}>
          <Button onClick={factoryDefaultsHandler} variant="outlined" color="warning">
            <Trans>Restablecer valores por defecto</Trans>
          </Button>
          <Box flexGrow={1}></Box>
          <Button
            onClick={resetHandler}
            disabled={!formState.isDirty}
            variant="outlined"
            color="info"
          >
            <Trans>Restablecer formulario</Trans>
          </Button>
          <LoadingButton
            type="submit"
            loading={formState.isSubmitting}
            disabled={!formState.isDirty}
            variant="contained"
          >
            <Trans>Guardar</Trans>
          </LoadingButton>
        </Box>
      </Box>
    </form>
  );
};

export default InterfaceTab;
